/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
'use strict';

const WebSocketRequest = require('./WebSocketRequest');

const {
	// Machine request types
	ADD_INBOX_MESSAGE,
	CREATE_STREAMSHEET_MESSAGE_TYPE,
	// DELETE_DATA,
	DELETE_MACHINE_MESSAGE_TYPE,
	DELETE_STREAMSHEET_MESSAGE_TYPE,
	GET_MACHINES_MESSAGE_TYPE,
	GET_CELL_RAW_VALUE,
	LOAD_MACHINE_MESSAGE_TYPE,
	UNLOAD_MACHINE_MESSAGE_TYPE,
	LOAD_SUBSCRIBE_MACHINE_MESSAGE_TYPE,
	MACHINE_UPDATE_SETTINGS_MESSAGE_TYPE,
	OPEN_MACHINE_MESSAGE_TYPE,
	PAUSE_MACHINE_MESSAGE_TYPE,
	RENAME_MACHINE_MESSAGE_TYPE,
	STREAMSHEET_STREAM_UPDATE_TYPE,
	UPDATE_MACHINE_IMAGE_MESSAGE_TYPE,
	UPDATE_MACHINE_TITLE_IMAGE_MESSAGE_TYPE,
	SAVE_MACHINE_AS_MESSAGE_TYPE,
	CONFIRM_LOAD_SUBSCRIBE_MACHINE_MESSAGE_TYPE,
	CONFIRM_PROCESSED_MACHINE_STEP,
	// SET_DATA,
	START_MACHINE_MESSAGE_TYPE,
	START_MACHINES_MESSAGE_TYPE,
	STEP_MACHINE_MESSAGE_TYPE,
	STOP_MACHINE_MESSAGE_TYPE,
	STOP_MACHINES_MESSAGE_TYPE,
	SUBSCRIBE_MACHINE_MESSAGE_TYPE,
	REDO_MESSAGE_TYPE,
	UNDO_MESSAGE_TYPE,
	UNSUBSCRIBE_MACHINE_MESSAGE_TYPE,
	// General request types
	COMMAND_MESSAGE_TYPE,
	MACHINE_ACTION_MESSAGE_TYPE,
	USER_LOGIN_MESSAGE_TYPE,
	USER_LOGOUT_MESSAGE_TYPE,
	SET_NAMED_CELLS
} = require('@cedalo/protocols').GatewayMessagingProtocol.MESSAGE_TYPES;

const { SERVICES_PERSISTENCE_INPUT } = require('@cedalo/protocols').Topics;

/**
 * ******************************************************************************************
 * User requests
 * ******************************************************************************************
 */
class UserLoginSocketRequest extends WebSocketRequest {
	constructor(ws, credentials) {
		super(ws, USER_LOGIN_MESSAGE_TYPE);
		this._credentials = credentials;
	}

	_getConfig() {
		return {
			topic: this._topic,
			credentials: this._credentials
		};
	}
}

class UserLogoutSocketRequest extends WebSocketRequest {
	constructor(ws, id) {
		super(ws, USER_LOGOUT_MESSAGE_TYPE);
		this._id = id;
	}

	_getConfig() {
		return {
			topic: this._topic,
			id: this._id
		};
	}
}

/**
 * ******************************************************************************************
 * Machine requests
 * ******************************************************************************************
 */

class CreateStreamSheetWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, activeItemId, settings, scope) {
		super(ws, CREATE_STREAMSHEET_MESSAGE_TYPE);
		this._machineId = machineId;
		this._activeItemId = activeItemId;
		this._settings = settings;
		this._scope = scope;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			activeItemId: this._activeItemId,
			settings: this._settings,
			scope: this._scope
		};
	}
}

class DeleteMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, scope) {
		super(ws, DELETE_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
		this._scope = scope;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			scope: this._scope
		};
	}
}

class UnloadMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, UNLOAD_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class DeleteStreamSheetWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, streamsheetId, scope) {
		super(ws, DELETE_STREAMSHEET_MESSAGE_TYPE);
		this._machineId = machineId;
		this._streamsheetId = streamsheetId;
		this._scope = scope;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			streamsheetId: this._streamsheetId,
			scope: this._scope
		};
	}
}

class GetMachinesWebSocketRequest extends WebSocketRequest {
	constructor(ws) {
		super(ws, GET_MACHINES_MESSAGE_TYPE);
	}

	_getConfig() {
		return {};
	}
}

class LoadMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, settings, scope) {
		super(ws, LOAD_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
		this._settings = settings;
		this._scope = scope;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			settings: this._settings,
			scope: this._scope
		};
	}
}

class LoadSubscribeMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, settings, scope) {
		super(ws, LOAD_SUBSCRIBE_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
		this._settings = settings;
		this._scope = scope;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			settings: this._settings,
			scope: this._scope
		};
	}
}

class OpenMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, OPEN_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class PauseMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, PAUSE_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class UpdateMachineImageWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, previewImage) {
		super(ws, UPDATE_MACHINE_IMAGE_MESSAGE_TYPE);
		this._topic = SERVICES_PERSISTENCE_INPUT;
		this._machineId = machineId;
		this._previewImage = previewImage;
	}

	_getConfig() {
		return {
			topic: this._topic,
			machineId: this._machineId,
			previewImage: this._previewImage
		};
	}
}

class UpdateMachineTitleImageWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, titleImage) {
		super(ws, UPDATE_MACHINE_TITLE_IMAGE_MESSAGE_TYPE);
		this._topic = SERVICES_PERSISTENCE_INPUT;
		this._machineId = machineId;
		this._titleImage = titleImage;
	}

	_getConfig() {
		return {
			topic: this._topic,
			machineId: this._machineId,
			titleImage: this._titleImage
		};
	}
}

class UpdateStreamSheetStreamsWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, streamsheetId, streams) {
		super(ws, STREAMSHEET_STREAM_UPDATE_TYPE);
		this._machineId = machineId;
		this._streamsheetId = streamsheetId;
		this._streams = streams;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			streamsheetId: this._streamsheetId,
			streams: this._streams
		};
	}
}

class RenameMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, newName) {
		super(ws, RENAME_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
		this._newName = newName;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			newName: this._newName
		};
	}
}

class SetNamedCellsWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, streamsheetId, namedCells) {
		super(ws, SET_NAMED_CELLS);
		this._machineId = machineId;
		this._streamsheetId = streamsheetId;
		this._namedCells = namedCells;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			streamsheetId: this._streamsheetId,
			namedCells: this._namedCells
		};
	}
}

class AddInboxMessageWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, streamsheetId, message, metadata) {
		super(ws, ADD_INBOX_MESSAGE);
		this._machineId = machineId;
		this._streamsheetId = streamsheetId;
		this._message = message;
		this._metadata = metadata;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			streamsheetId: this._streamsheetId,
			message: this._message,
			metadata: this._metadata
		};
	}
}

class SaveMachineAsWebSocketRequest extends WebSocketRequest {
	constructor(ws, originalMachineId, newName) {
		super(ws, SAVE_MACHINE_AS_MESSAGE_TYPE);
		this._originalMachineId = originalMachineId;
		this._newName = newName;
	}

	_getConfig() {
		return {
			originalMachineId: this._originalMachineId,
			newName: this._newName
		};
	}
}

class ConfirmLoadSubscribeMachineRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, CONFIRM_LOAD_SUBSCRIBE_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class ConfirmProcessedMachineStepRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, CONFIRM_PROCESSED_MACHINE_STEP);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class StartMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, START_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class StartMachinesWebSocketRequest extends WebSocketRequest {
	constructor(ws) {
		super(ws, START_MACHINES_MESSAGE_TYPE);
	}

	_getConfig() {
		return {};
	}
}

class StepMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, STEP_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class StopMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, STOP_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class StopMachinesWebSocketRequest extends WebSocketRequest {
	constructor(ws) {
		super(ws, STOP_MACHINES_MESSAGE_TYPE);
	}

	_getConfig() {
		return {};
	}
}

class SubscribeMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, SUBSCRIBE_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}
class UnsubscribeMachineWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, UNSUBSCRIBE_MACHINE_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class RedoWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, REDO_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class UndoWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId) {
		super(ws, UNDO_MESSAGE_TYPE);
		this._machineId = machineId;
	}

	_getConfig() {
		return {
			machineId: this._machineId
		};
	}
}

class UpdateMachineSettingsWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, settings) {
		super(ws, MACHINE_UPDATE_SETTINGS_MESSAGE_TYPE);
		this._machineId = machineId;
		this._settings = settings;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			settings: this._settings
		};
	}
}

class GetCellRawValueWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, streamsheetId, reference) {
		super(ws, GET_CELL_RAW_VALUE);
		this._machineId = machineId;
		this._streamsheetId = streamsheetId;
		this._reference = reference;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			streamsheetId: this._streamsheetId,
			reference: this._reference
		};
	}
}

/**
 * ******************************************************************************************
 * General requests
 * ******************************************************************************************
 */
class CommandWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, command) {
		super(ws, COMMAND_MESSAGE_TYPE);
		this._machineId = machineId;
		this._command = command;
	}

	_getConfig() {
		return { machineId: this._machineId, command: this._command };
	}
}

class MachineActionWebSocketRequest extends WebSocketRequest {
	constructor(ws, machineId, action) {
		super(ws, MACHINE_ACTION_MESSAGE_TYPE);
		this._machineId = machineId;
		this._action = action;
	}

	_getConfig() {
		return {
			machineId: this._machineId,
			action: this._action
		};
	}
}

module.exports = {
	// Graph requests
	// Machine requests
	AddInboxMessageWebSocketRequest,
	CreateStreamSheetWebSocketRequest,
	DeleteMachineWebSocketRequest,
	DeleteStreamSheetWebSocketRequest,
	GetMachinesWebSocketRequest,
	LoadMachineWebSocketRequest,
	LoadSubscribeMachineWebSocketRequest,
	OpenMachineWebSocketRequest,
	PauseMachineWebSocketRequest,
	RenameMachineWebSocketRequest,
	UpdateMachineImageWebSocketRequest,
	UpdateMachineTitleImageWebSocketRequest,
	UpdateMachineSettingsWebSocketRequest,
	UpdateStreamSheetStreamsWebSocketRequest,
	SaveMachineAsWebSocketRequest,
	ConfirmLoadSubscribeMachineRequest,
	ConfirmProcessedMachineStepRequest,
	SetNamedCellsWebSocketRequest,
	StartMachineWebSocketRequest,
	StartMachinesWebSocketRequest,
	StepMachineWebSocketRequest,
	StopMachineWebSocketRequest,
	StopMachinesWebSocketRequest,
	SubscribeMachineWebSocketRequest,
	RedoWebSocketRequest,
	UndoWebSocketRequest,
	UnloadMachineWebSocketRequest,
	UnsubscribeMachineWebSocketRequest,
	GetCellRawValueWebSocketRequest,
	// General requests
	CommandWebSocketRequest,
	MachineActionWebSocketRequest,
	UserLoginSocketRequest,
	UserLogoutSocketRequest
};
